/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    Select,
    CircularProgress,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    InputLabel,
    OutlinedInput,
} from "@mui/material";
import {
    Refresh,
    OpenInNew,
    PriorityHighOutlined,
    PersonAddAltOutlined,
    TrendingUpOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchRecoveryQueue,
    fetchRecoveryQueueSummary,
    assignInvoice,
    manualEscalateInvoice,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectRecoveryQueue,
    selectRecoveryQueueCursor,
    selectIsLoadingRecoveryQueue,
    selectRecoveryQueueError,
    selectRecoveryQueueSummary,
    selectIsLoadingRecoveryQueueSummary,
    selectIsAssigningInvoice,
    selectIsManualEscalating,
} from "../../redux/selectors/collectSelector";
import {
    ICollectInvoice,
    RecoveryState,
    EscalationLevel,
    IRecoveryQueueQuery,
} from "../../utility/types/collectTypes";
import {
    fetchBusinessMembers,
    IBusinessMember,
} from "../../redux/services/collectTaskService";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectRecoveryQueue (Stage 2C) — prioritized list of in-recovery invoices
// driven by the escalation engine. Operators triage from this view: open,
// reassign, or manually escalate problem invoices.
// ─────────────────────────────────────────────────────────────────────────

const RECOVERY_STATE_OPTIONS: Array<{ value: RecoveryState; label: string }> = [
    { value: "overdue", label: "Overdue" },
    { value: "active_recovery", label: "Active recovery" },
    { value: "partially_paid", label: "Partially paid" },
    { value: "promise_to_pay", label: "Promise to pay" },
    { value: "manual_review", label: "Manual review" },
    { value: "legal_action", label: "Legal action" },
    { value: "disputed", label: "Disputed" },
];

const ESCALATION_LEVEL_OPTIONS: Array<{ value: EscalationLevel; label: string }> = [
    { value: "friendly", label: "Friendly" },
    { value: "professional", label: "Professional" },
    { value: "firm", label: "Firm" },
    { value: "manual_review", label: "Manual review" },
];

const STATE_CHIP_COLOR: Record<RecoveryState, string> = {
    monitoring: "#64748b",
    due_soon: "#0284c7",
    due_today: "#0369a1",
    overdue: "#dc2626",
    active_recovery: "#d97706",
    promise_to_pay: "#2563eb",
    partially_paid: "#7c3aed",
    disputed: "#ea580c",
    manual_review: "#b91c1c",
    legal_action: "#991b1b",
    recovered: "#16a34a",
    closed: "#475569",
};

const LEVEL_CHIP_COLOR: Record<EscalationLevel, string> = {
    friendly: "#16a34a",
    professional: "#0284c7",
    firm: "#d97706",
    manual_review: "#b91c1c",
};

function formatMoney(value: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
        }).format(value || 0);
    } catch {
        return `$${(value || 0).toFixed(2)}`;
    }
}

function formatAssignee(
    assigned: ICollectInvoice["assignedToUserId"],
): string {
    if (!assigned) return "Unassigned";
    if (typeof assigned === "string") return "Assigned";
    const name = [assigned.firstName, assigned.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
    return name || assigned.email || "Assigned";
}

function humanLabel(value: string): string {
    return value
        .split("_")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(" ");
}

function memberLabel(m: IBusinessMember): string {
    const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim();
    return name || m.email || "Unknown";
}

const CollectRecoveryQueue: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();

    const businessId = useSelector(selectActiveBusinessId);
    const queue = useSelector(selectRecoveryQueue);
    const cursor = useSelector(selectRecoveryQueueCursor);
    const isLoading = useSelector(selectIsLoadingRecoveryQueue);
    const error = useSelector(selectRecoveryQueueError);
    const summary = useSelector(selectRecoveryQueueSummary);
    const isLoadingSummary = useSelector(selectIsLoadingRecoveryQueueSummary);
    const isAssigning = useSelector(selectIsAssigningInvoice);
    const isEscalating = useSelector(selectIsManualEscalating);

    // ── Filter state ────────────────────────────────────────────────
    const [stateFilter, setStateFilter] = useState<RecoveryState[]>([]);
    const [levelFilter, setLevelFilter] = useState<EscalationLevel[]>([]);
    const [assignedFilter, setAssignedFilter] = useState<string>("any"); // "any" | "unassigned"
    const [minBalance, setMinBalance] = useState<string>("");
    const [minDaysOverdue, setMinDaysOverdue] = useState<string>("");

    // ── Assign dialog state ─────────────────────────────────────────
    const [assignTarget, setAssignTarget] = useState<ICollectInvoice | null>(
        null,
    );
    const [assigneeUserId, setAssigneeUserId] = useState<string>("");

    // Team members for the assignee picker.
    const [members, setMembers] = useState<IBusinessMember[]>([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);

    const buildQuery = (
        overrides: Partial<IRecoveryQueueQuery> = {},
    ): IRecoveryQueueQuery => {
        const q: IRecoveryQueueQuery = {};
        if (stateFilter.length) q.state = stateFilter;
        if (levelFilter.length) q.escalationLevel = levelFilter;
        if (assignedFilter === "unassigned") q.assignedToUserId = "unassigned";
        const mb = parseFloat(minBalance);
        if (!Number.isNaN(mb) && mb > 0) q.minBalance = mb;
        const md = parseInt(minDaysOverdue, 10);
        if (!Number.isNaN(md) && md > 0) q.minDaysOverdue = md;
        return { ...q, ...overrides };
    };

    const loadInitial = () => {
        if (!businessId) return;
        dispatch(fetchRecoveryQueue(buildQuery()));
        dispatch(fetchRecoveryQueueSummary({}));
    };

    useEffect(() => {
        loadInitial();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    // Load the team members once per business so the assign picker can show
    // real first/last names instead of raw user IDs.
    useEffect(() => {
        if (!businessId) return;
        let cancelled = false;
        setIsLoadingMembers(true);
        fetchBusinessMembers(businessId)
            .then((mems) => {
                if (!cancelled) setMembers(mems);
            })
            .catch(() => {
                if (!cancelled) setMembers([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingMembers(false);
            });
        return () => {
            cancelled = true;
        };
    }, [businessId]);

    const handleApplyFilters = () => {
        if (!businessId) return;
        dispatch(fetchRecoveryQueue(buildQuery()));
    };

    const handleLoadMore = () => {
        if (!businessId || !cursor) return;
        dispatch(fetchRecoveryQueue(buildQuery({ cursor })));
    };

    const handleRefresh = () => {
        loadInitial();
    };

    const handleOpenInvoice = (id: string) => {
        navigate(`/collect/invoices/${id}`);
    };

    const handleOpenAssign = (inv: ICollectInvoice) => {
        setAssignTarget(inv);
        setAssigneeUserId(
            typeof inv.assignedToUserId === "string"
                ? inv.assignedToUserId
                : inv.assignedToUserId?._id || "",
        );
    };

    const handleCloseAssign = () => {
        if (isAssigning) return;
        setAssignTarget(null);
        setAssigneeUserId("");
    };

    const handleSubmitAssign = async (unassign: boolean) => {
        if (!assignTarget) return;
        const action = await dispatch(
            assignInvoice({
                invoiceId: assignTarget._id,
                assigneeUserId: unassign ? null : assigneeUserId.trim(),
            }),
        );
        if (assignInvoice.fulfilled.match(action)) {
            toast.success(
                unassign ? "Invoice unassigned." : "Invoice assigned.",
            );
            handleCloseAssign();
            dispatch(fetchRecoveryQueueSummary({}));
        } else {
            toast.error(
                (action.payload as string) || "Failed to update assignment.",
            );
        }
    };

    const handleEscalate = async (inv: ICollectInvoice) => {
        const action = await dispatch(
            manualEscalateInvoice({ invoiceId: inv._id }),
        );
        if (manualEscalateInvoice.fulfilled.match(action)) {
            toast.success("Escalation evaluated.");
            // Refresh queue + summary to reflect any tone/state advance.
            dispatch(fetchRecoveryQueue(buildQuery()));
            dispatch(fetchRecoveryQueueSummary({}));
        } else {
            toast.error(
                (action.payload as string) || "Failed to escalate invoice.",
            );
        }
    };

    const totals = summary?.totals;
    const byState = summary?.byState || [];
    const byLevel = summary?.byEscalationLevel || [];

    const stateCountLookup = useMemo(() => {
        const m: Record<string, number> = {};
        for (const r of byState) m[r.state] = r.count;
        return m;
    }, [byState]);

    const levelCountLookup = useMemo(() => {
        const m: Record<string, number> = {};
        for (const r of byLevel) m[r.level] = r.count;
        return m;
    }, [byLevel]);

    return (
        <CollectPageWrapper>
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
                {/* ── Summary header ───────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3 }}>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>Recovery queue</CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Invoices ordered by priority score. Higher score = act first.
                            </CollectSectionSubtitle>
                        </Box>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={isLoading}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </CollectSectionHeader>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                            <CollectKpiCard>
                                <CollectKpiLabel>Invoices in queue</CollectKpiLabel>
                                <CollectKpiValue>
                                    {isLoadingSummary ? "…" : (totals?.count ?? 0)}
                                </CollectKpiValue>
                                <CollectKpiHint>Across all recovery states</CollectKpiHint>
                            </CollectKpiCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CollectKpiCard>
                                <CollectKpiLabel>Total outstanding</CollectKpiLabel>
                                <CollectKpiValue>
                                    {isLoadingSummary
                                        ? "…"
                                        : formatMoney(totals?.totalBalance ?? 0)}
                                </CollectKpiValue>
                                <CollectKpiHint>Balance across queue</CollectKpiHint>
                            </CollectKpiCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CollectKpiCard>
                                <CollectKpiLabel>Avg priority score</CollectKpiLabel>
                                <CollectKpiValue>
                                    {isLoadingSummary
                                        ? "…"
                                        : Math.round(totals?.avgPriorityScore ?? 0)}
                                </CollectKpiValue>
                                <CollectKpiHint>0 – 100</CollectKpiHint>
                            </CollectKpiCard>
                        </Grid>
                    </Grid>

                    {(byState.length > 0 || byLevel.length > 0) && (
                        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {RECOVERY_STATE_OPTIONS.map((opt) => (
                                <Chip
                                    key={`s-${opt.value}`}
                                    size="small"
                                    label={`${opt.label}: ${stateCountLookup[opt.value] || 0}`}
                                    sx={{
                                        backgroundColor: `${STATE_CHIP_COLOR[opt.value]}1A`,
                                        color: STATE_CHIP_COLOR[opt.value],
                                        fontWeight: 600,
                                    }}
                                />
                            ))}
                            {ESCALATION_LEVEL_OPTIONS.map((opt) => (
                                <Chip
                                    key={`l-${opt.value}`}
                                    size="small"
                                    label={`${opt.label}: ${levelCountLookup[opt.value] || 0}`}
                                    sx={{
                                        backgroundColor: `${LEVEL_CHIP_COLOR[opt.value]}1A`,
                                        color: LEVEL_CHIP_COLOR[opt.value],
                                        fontWeight: 600,
                                    }}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}
                </CollectSectionCard>

                {/* ── Filters ─────────────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Recovery state</InputLabel>
                                <Select
                                    multiple
                                    value={stateFilter}
                                    onChange={(e) =>
                                        setStateFilter(
                                            (typeof e.target.value === "string"
                                                ? e.target.value.split(",")
                                                : e.target.value) as RecoveryState[],
                                        )
                                    }
                                    input={<OutlinedInput label="Recovery state" />}
                                    renderValue={(sel) =>
                                        (sel as string[]).map(humanLabel).join(", ")
                                    }
                                >
                                    {RECOVERY_STATE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Escalation level</InputLabel>
                                <Select
                                    multiple
                                    value={levelFilter}
                                    onChange={(e) =>
                                        setLevelFilter(
                                            (typeof e.target.value === "string"
                                                ? e.target.value.split(",")
                                                : e.target.value) as EscalationLevel[],
                                        )
                                    }
                                    input={<OutlinedInput label="Escalation level" />}
                                    renderValue={(sel) =>
                                        (sel as string[]).map(humanLabel).join(", ")
                                    }
                                >
                                    {ESCALATION_LEVEL_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Assignment"
                                value={assignedFilter}
                                onChange={(e) => setAssignedFilter(e.target.value)}
                            >
                                <MenuItem value="any">Any</MenuItem>
                                <MenuItem value="unassigned">Unassigned</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Min balance"
                                type="number"
                                value={minBalance}
                                onChange={(e) => setMinBalance(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Min days overdue"
                                type="number"
                                value={minDaysOverdue}
                                onChange={(e) => setMinDaysOverdue(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FlexBetween>
                                <Box />
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <CollectGhostButton
                                        onClick={() => {
                                            setStateFilter([]);
                                            setLevelFilter([]);
                                            setAssignedFilter("any");
                                            setMinBalance("");
                                            setMinDaysOverdue("");
                                        }}
                                    >
                                        Clear
                                    </CollectGhostButton>
                                    <CollectPrimaryButton onClick={handleApplyFilters}>
                                        Apply filters
                                    </CollectPrimaryButton>
                                </Box>
                            </FlexBetween>
                        </Grid>
                    </Grid>
                </CollectSectionCard>

                {/* ── Table ───────────────────────────────────── */}
                <CollectSectionCard>
                    {error && (
                        <Box sx={{ p: 2, color: "#b91c1c" }}>
                            <Typography variant="body2">{error}</Typography>
                        </Box>
                    )}

                    <CollectTableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <CollectTableHeadCell>Invoice</CollectTableHeadCell>
                                    <CollectTableHeadCell>Customer</CollectTableHeadCell>
                                    <CollectTableHeadCell align="right">
                                        Balance
                                    </CollectTableHeadCell>
                                    <CollectTableHeadCell align="right">
                                        Days overdue
                                    </CollectTableHeadCell>
                                    <CollectTableHeadCell>State</CollectTableHeadCell>
                                    <CollectTableHeadCell>Level</CollectTableHeadCell>
                                    <CollectTableHeadCell align="right">
                                        Priority
                                    </CollectTableHeadCell>
                                    <CollectTableHeadCell>Assigned</CollectTableHeadCell>
                                    <CollectTableHeadCell align="right">
                                        Actions
                                    </CollectTableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {queue.map((inv) => {
                                    const state = inv.recoveryState || "overdue";
                                    const level = inv.escalationLevel || "friendly";
                                    return (
                                        <TableRow key={inv._id} hover>
                                            <CollectTableBodyCell>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: colors.primary,
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => handleOpenInvoice(inv._id)}
                                                >
                                                    {inv.docNumber || inv._id.slice(-6)}
                                                </Typography>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {inv.customer?.displayName ||
                                                    inv.customerDisplayName ||
                                                    "—"}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                {formatMoney(
                                                    inv.balance,
                                                    inv.currency || "USD",
                                                )}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                {inv.daysOverdue ?? 0}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Chip
                                                    size="small"
                                                    label={humanLabel(state)}
                                                    sx={{
                                                        backgroundColor: `${STATE_CHIP_COLOR[state]}1A`,
                                                        color: STATE_CHIP_COLOR[state],
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={humanLabel(level)}
                                                    sx={{
                                                        color: LEVEL_CHIP_COLOR[level],
                                                        borderColor:
                                                            LEVEL_CHIP_COLOR[level],
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <Typography
                                                    component="span"
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {Math.round(inv.priorityScore ?? 0)}
                                                </Typography>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {formatAssignee(inv.assignedToUserId)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <Tooltip title="Escalate">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEscalate(inv)}
                                                            disabled={isEscalating}
                                                        >
                                                            <TrendingUpOutlined fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Assign">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenAssign(inv)}
                                                    >
                                                        <PersonAddAltOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Open invoice">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenInvoice(inv._id)}
                                                    >
                                                        <OpenInNew fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CollectTableContainer>

                    {!isLoading && queue.length === 0 && (
                        <CollectEmptyState>
                            <PriorityHighOutlined
                                sx={{ fontSize: 40, color: "#94a3b8" }}
                            />
                            <CollectEmptyTitle>
                                {businessId
                                    ? "Queue is clear"
                                    : "Select a business"}
                            </CollectEmptyTitle>
                            <CollectEmptyHint>
                                {businessId
                                    ? "No invoices currently match the recovery filters."
                                    : "Pick an active business from the top nav to load the queue."}
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    )}

                    {isLoading && (
                        <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {cursor && !isLoading && (
                        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                            <CollectGhostButton onClick={handleLoadMore}>
                                Load more
                            </CollectGhostButton>
                        </Box>
                    )}
                </CollectSectionCard>
            </Box>

            {/* ── Assign dialog ──────────────────────────────── */}
            <Dialog
                open={Boolean(assignTarget)}
                onClose={handleCloseAssign}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Assign invoice</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: "#64748b" }}>
                        Invoice{" "}
                        <strong>
                            {assignTarget?.docNumber ||
                                assignTarget?._id.slice(-6) ||
                                "—"}
                        </strong>
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel id="recovery-assignee-label">
                            Assign to
                        </InputLabel>
                        <Select
                            labelId="recovery-assignee-label"
                            label="Assign to"
                            value={assigneeUserId}
                            onChange={(e) =>
                                setAssigneeUserId(String(e.target.value))
                            }
                            disabled={isAssigning || isLoadingMembers}
                        >
                            <MenuItem value="">
                                <em>Unassigned</em>
                            </MenuItem>
                            {members.map((m) => (
                                <MenuItem key={m.userId} value={m.userId}>
                                    {memberLabel(m)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography
                        variant="caption"
                        sx={{ mt: 1, display: "block", color: "#94a3b8" }}
                    >
                        {isLoadingMembers
                            ? "Loading team members…"
                            : members.length === 0
                                ? "No team members found for this business."
                                : "Pick a team member to own this invoice."}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton
                        onClick={() => handleSubmitAssign(true)}
                        disabled={isAssigning}
                    >
                        Unassign
                    </CollectGhostButton>
                    <CollectGhostButton onClick={handleCloseAssign} disabled={isAssigning}>
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={() => handleSubmitAssign(false)}
                        disabled={isAssigning || !assigneeUserId.trim()}
                    >
                        {isAssigning ? "Saving…" : "Assign"}
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectRecoveryQueue;
